import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { selectZefProgressByType } from '@zerops/zef/progress';
import { zefDialogOpen } from '@zerops/zef/dialog';
import { selectZefNgrxRouterQueryParams } from '@zerops/zef/ngrx-router';
import { fadeInAnimation } from '@zerops/zef/animations';
import { DynamicPopAnchorDirective } from '@zerops/zui/dynamic-pop-anchor';
import { ObservableInput } from 'observable-input';
import orderBy from 'lodash-es/orderBy';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, mergeMap, pluck, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { requestRecipeAdd, requestShowYaml } from './recipes.action';
import { RecipeAttributes, RecipesService, RecipeTagAttributes, StrapiData } from './recipes.service';

@Component({
  selector: 'zui-recipes',
  templateUrl: './recipes.feature.html',
  styleUrls: [ './recipes.feature.scss' ],
  animations: [ fadeInAnimation ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecipesFeature extends ZefReactiveComponent {

  // # Event Stream
  onImportProject$ = new Subject<{
    item: StrapiData<RecipeAttributes>;
    ref: DynamicPopAnchorDirective;
    devEnv: boolean;
    prodEnv: boolean;
    anchor: HTMLElement;
  }>();
  onShowProjectYaml$ = new Subject<{ data: StrapiData<RecipeAttributes>; mode: 'ha' | 'non-ha' }>();
  onSearchValueChange$ = new BehaviorSubject(undefined);

  // # Data
  // -- angular
  @ViewChild('diagramRef')
  diagramRef: ElementRef<any>;

  @ObservableInput()
  @Input('projectImportLoadingKey')
  projectImportLoadingKey$!: Observable<string>;

  @ObservableInput()
  @Input('isAuthorized')
  isAuthorized$!: Observable<boolean>;

  @Input()
  showSearch = true;

  @Input()
  authPopKey: string;

  @Output()
  showYaml = new EventEmitter<string>();

  // -- async
  data$ = this._recipesService.selectList$();
  showAllRecipes$ = new BehaviorSubject<boolean>(false);
  routerParams$ = this._store.pipe(select(selectZefNgrxRouterQueryParams));
  activeFilterTagsParam$ = this.routerParams$.pipe(
    pluck('tags'),
    distinctUntilChanged()
  );
  activeFilterTags$ = this.activeFilterTagsParam$.pipe(
    map((d) => d?.split(',') as string[] || [])
  );
  activeFilterTagsMap$ = this.activeFilterTags$.pipe(
    map((d) => d.reduce((obj, itm) => { obj[itm] = true; return obj; }, {}))
  );
  filteredRecipes$ = combineLatest([
    this.data$,
    this.activeFilterTagsMap$,
    this.activeFilterTags$.pipe(map((d) => d?.length))
  ]).pipe(
    map(([ data, activeFilterTagsMap, activeFilterLen ]) => {

      const res = data.reduce((obj, itm) => {

        const matches = itm.attributes.tags.data.filter(
          (t) => activeFilterTagsMap[t.attributes.slug]
        )?.length;

        if (matches > 0) {

          obj.data.push(itm);

          let weight = 0;

          if (activeFilterLen >= 2) {

            if (matches === 1 && itm.attributes.tags?.data.length === 2) {
              weight = 1 / 3;
            } else {
              weight = (matches / activeFilterLen);
            }

            weight = (matches / itm.attributes.tags?.data.length) + weight;

            if ((itm.attributes.tags?.data.length >= 2) && (matches === activeFilterLen)) {
              weight = weight + 1;
            }

          }

          obj.weightMap[itm.id] = weight;

        }

        return obj;
      }, {
        data: [],
        weightMap: {}
      });

      const orderedData = orderBy(
        res.data,
        [ (itm) => (res.weightMap[itm.id] || 0) ],
        [ 'desc' ]
      );

      return (!!orderedData?.length
        ? orderedData
        : data) as StrapiData<RecipeAttributes>[];
    })
  );
  visibleFilteredRecipes$ = combineLatest([
    this.showAllRecipes$,
    this.filteredRecipes$
  ]).pipe(
    map(([ showAll, data ]) => showAll ? data : data.slice(0, 3))
  );
  recipeTags$ = this._recipesService.selectRecipeTags$();
  recipeTagsAttributesMap$ = this._recipesService.selectRecipeTagsAttributesMap$();
  filterGroups$ = this.recipeTags$.pipe(map((d) => {
    return d.reduce((obj, itm) => {
      obj[itm.attributes.type].push(itm);
      return obj;
    }, {
      application: [] as StrapiData<RecipeTagAttributes>[],
      runtime: [] as StrapiData<RecipeTagAttributes>[],
      other: [] as StrapiData<RecipeTagAttributes>[]
    })
  }));
  enabledTagsMap$ = this.filteredRecipes$.pipe(
    map((d) => d.reduce((obj, itm) => {
      itm.attributes.tags.data.forEach((t) => obj[t.attributes.slug] = true);
      return obj;
    }, {}))
  );
  isLoading$ = this._recipesService.selectLoading$();
  projectImportProgress$ = this.projectImportLoadingKey$.pipe(
    filter((key) => !!key),
    switchMap((key) => this._store.pipe(select(selectZefProgressByType(key))))
  );

  // # State resolver
  state = this.$connect({
    isLoading: this.isLoading$,
    filteredRecipes: this.filteredRecipes$,
    visibleFilteredRecipes: this.visibleFilteredRecipes$,
    showAllRecipes: this.showAllRecipes$,
    activeFilterTagsParam: this.activeFilterTagsParam$,
    projectImportProgress: this.projectImportProgress$,
    recipeTags: this.recipeTags$,
    filterGroups: this.filterGroups$,
    activeFilterTags: this.activeFilterTags$,
    activeFilterTagsMap: this.activeFilterTagsMap$,
    routerParams: this.routerParams$,
    enabledTagsMap: this.enabledTagsMap$
  });

  // # Actions
  private _requestRecipeAddAction$ = this.onImportProject$.pipe(
    withLatestFrom(this.isAuthorized$),
    tap(([ { ref, anchor }, isAuthorized]) => {
      if (!isAuthorized) {
        ref.anchor(anchor);
      }
    }),
    mergeMap(([ { item, devEnv, prodEnv }, isAuthorized ]) => {
      const nonHaRecipe = {
        id: item.id,
        yaml: item.attributes.config_nonha
      };
      const haRecipe = {
        id: item.id,
        yaml: item.attributes.config_ha
      };

      // if user is logged in, add recipe immediately
      if (isAuthorized) {

        const recipeActions = [];
        const nonHaRecipeAction = requestRecipeAdd(nonHaRecipe);
        const haRecipeAction = requestRecipeAdd(haRecipe);

        if (devEnv) { recipeActions.push(nonHaRecipeAction); }
        if (prodEnv) { recipeActions.push(haRecipeAction); }

        return recipeActions;

      } else {
        // otherwise open auth pop anchored in the `tap()` above
        return [
          zefDialogOpen({
            key: this.authPopKey,
            meta: { nonHaRecipeId: devEnv ? nonHaRecipe?.id : undefined, haRecipeId: prodEnv ? haRecipe?.id : undefined }
          })
        ];
      }

    })
  );

  private _requestShowYamlAction$ = this.onShowProjectYaml$.pipe(
    map(({ data, mode }) => requestShowYaml({ yaml: mode === 'ha' ? data.attributes.config_ha : data.attributes.config_nonha }))
  );

  constructor(
    private _recipesService: RecipesService,
    private _store: Store<any>,
    private _elementRef: ElementRef
  ) {
    super();

    combineLatest([
      this.recipeTagsAttributesMap$,
      this.activeFilterTagsMap$,
      this.showAllRecipes$
    ])
      .pipe(delay(0),takeUntil(this.onDestroy$))
      .subscribe(([ data, activeTags ]) => {
        const els = this._elementRef.nativeElement.getElementsByTagName('ze-recipe-tag');

        if (els?.length) {
          for (const el of els) {
            el?.setData(data, activeTags);
          }
        }
      });

    this.$dispatchActions([
      this._requestRecipeAddAction$,
      this._requestShowYamlAction$
    ])

  }

}
